import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAsync } from 'react-use';

import { GrafanaTheme2 } from '@grafana/data';
import { getBackendSrv, PluginPage } from '@grafana/runtime';
import { Alert, Button, Card, Icon, useStyles2 } from '@grafana/ui';

import { css } from '@emotion/css';

import { PLUGIN_ROOT } from 'consts';

import { Highlight } from './Highlight';

export default function Home(): React.JSX.Element | null {
  const styles = useStyles2(getStyles);
  const navigate = useNavigate();

  const llmSettings = useAsync(async () => {
    const result = await getBackendSrv().get('/api/plugins/grafana-llm-app/settings', undefined, undefined, {
      showSuccessAlert: false,
      showErrorAlert: false,
    });
    return {
      allowed: result.enabled,
      enabled: !result.jsonData?.disabled === true,
      error: false,
    };
  });

  const showLLMAlert =
    llmSettings.loading === false &&
    (llmSettings.value?.error ?? false) === false &&
    llmSettings.value?.enabled === false &&
    llmSettings.value?.allowed !== false;

  return (
    <PluginPage>
      {showLLMAlert ? (
        <Alert title="LLM integration not configured" severity="info">
          <div className={styles.alert}>
            Grafana uses LLMs to power many useful features, like tracing analysis and incident summaries. These
            features will not be available until you have set up an LLM integration
            <Button
              variant="secondary"
              onClick={() => {
                navigate(`/plugins/grafana-llm-app`);
              }}
            >
              Configure
            </Button>
          </div>
        </Alert>
      ) : null}
      <div className={styles.topRowContainer}>
        <Card className={styles.infoCard}>
          <Card.Heading>Getting started</Card.Heading>
          <Card.Description className={styles.cardDescription}>
            <ul className={styles.cardList}>
              <li className={styles.cardListItem}>
                Visit our{' '}
                <a
                  className={styles.cardLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://grafana.com/docs/grafana-cloud/alerting-and-irm/machine-learning/dynamic-alerting/forecasting/"
                >
                  getting started
                  <Icon name="external-link-alt" />
                </a>{' '}
                tutorial to see metric predictions in action
              </li>
              <li className={styles.cardListItem}>
                <a
                  className={styles.cardLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://grafana.com/docs/grafana-cloud/alerting-and-irm/machine-learning/dynamic-alerting/querying/"
                >
                  Querying
                  <Icon name="external-link-alt" />
                </a>{' '}
                will help you craft queries using the ML Prometheus datasource
              </li>
              <li className={styles.cardListItem}>
                <a
                  className={styles.cardLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://grafana.com/docs/grafana-cloud/alerting-and-irm/machine-learning/dynamic-alerting/forecasting/config/"
                >
                  Model configuration
                  <Icon name="external-link-alt" />
                </a>{' '}
                documents how you can tune models to improve predctions
              </li>
            </ul>
          </Card.Description>
        </Card>

        <Card
          className={styles.infoCard}
          onClick={() => {
            navigate(`${PLUGIN_ROOT}/metric-forecast`);
          }}
        >
          <Card.Heading>Forecasts & anomaly detection</Card.Heading>
          <Card.Description className={styles.cardDescription}>
            Find anomalies and plan ahead by predicting current and future values for timeseries based on historical
            data
            <a className={styles.cardLink} href={`${PLUGIN_ROOT}/metric-forecast`}>
              Manage forecasts
            </a>
          </Card.Description>
        </Card>

        <Card
          className={styles.infoCard}
          onClick={() => {
            navigate(`${PLUGIN_ROOT}/outlier-detector`);
          }}
        >
          <Card.Heading>Outlier detection</Card.Heading>
          <Card.Description className={styles.cardDescription}>
            Detect when some members of a group are behaving differently from the rest
            <a className={styles.cardLink} href={`${PLUGIN_ROOT}/metric-forecast`}>
              Manage outliers
            </a>
          </Card.Description>
        </Card>

        <Card
          className={styles.infoCard}
          onClick={() => {
            navigate(`${PLUGIN_ROOT}/investigations`);
          }}
        >
          <Card.Heading>Sift investigations</Card.Heading>
          <Card.Description className={styles.cardDescription}>
            Run checks to find unusual system activity
            <a className={styles.cardLink} href={`${PLUGIN_ROOT}/metric-forecast`}>
              Manage Sift investigations
            </a>
          </Card.Description>
        </Card>
      </div>

      <div className={styles.highlightIntro}>
        Grafana Cloud makes use of AI-powered tools and machine learning capabilities across our platform. Check out
        some of the highlights below to learn more about all the ways we put AI and ML to work for you.
      </div>

      <Highlight
        title="Adaptive telemetry"
        learnMoreLink="https://grafana.com/docs/grafana-cloud/cost-management-and-billing/adaptive-telemetry/"
      >
        <div className={styles.highlightDetails}>
          Tools to optimize which signals are stored, ensuring you only keep the most valuable telemetry. Through
          classification and prioritization, Adaptive Telemetry (which includes Adaptive Metrics and Adaptive Logs)
          helps you lower costs without sacrificing the insights you need to keep your systems running smoothly.
        </div>
      </Highlight>

      <Highlight title="SLO monitoring" learnMoreLink="https://grafana.com/docs/grafana-cloud/alerting-and-irm/slo/">
        <div className={styles.highlightDetails}>
          With statistical techniques, Grafana Cloud can assess the probability that you may reach a threshold within
          one of your SLOs
        </div>
      </Highlight>

      <Highlight
        title="Flamegraph AI"
        learnMoreLink="https://grafana.com/docs/grafana-cloud/monitor-applications/profiles/flamegraph-ai/"
      >
        <div className={styles.highlightDetails}>
          Understand performance bottlenecks by analyzing continuous profiling data and highlighting the most
          significant patterns and anomalies in performance
        </div>
      </Highlight>

      <Highlight
        title="Incident summaries"
        learnMoreLink="https://grafana.com/docs/grafana-cloud/alerting-and-irm/irm/incident/intro/"
      >
        <div className={styles.highlightDetails}>
          To make incident resolution simpler, Grafana Cloud will suggest an AI generated synopsis of the incident that
          captures the key details from your incident logs and timeline with a single click
        </div>
      </Highlight>

      <Highlight title="Dashboard assistant" learnMoreLink="https://grafana.com/docs/grafana/latest/dashboards/">
        <div className={styles.highlightDetails}>
          When creating or editing a panel or dashboard, this assistant will suggest a relevant title and description
          based on the contents provided
        </div>
      </Highlight>

      <Highlight
        title="Drilldown metrics & logs"
        learnMoreLink="https://grafana.com/docs/grafana-cloud/visualizations/simplified-exploration/"
      >
        <div className={styles.highlightDetails}>
          When filtering by a label, graphs are sorted by relevancy and outlying values, so you see what is important
          first
        </div>
      </Highlight>

      <Highlight
        title="AI observability"
        learnMoreLink="https://grafana.com/blog/2024/10/21/monitor-your-generative-ai-app-with-the-ai-observability-solution-in-grafana-cloud/"
      >
        <div className={styles.highlightDetails}>
          Through integrations, Grafana Cloud offers a set of dashboards to monitor your LLMs performance and your GPUs
          <a
            className={styles.highlightLink}
            target="_blank"
            rel="noopener noreferrer"
            href="https://grafana.com/docs/grafana-cloud/monitor-applications/ai-observability/instrument/"
          >
            Getting started with AI observability <Icon name="external-link-alt" />
          </a>
        </div>
      </Highlight>

      <div className={styles.highlightIntro}>
        To learn more about AI/ML in Grafana Cloud, check out the{' '}
        <a
          className={styles.highlightLink}
          target="_blank"
          rel="noopener noreferrer"
          href="https://grafana.com/docs/grafana-cloud/alerting-and-irm/machine-learning/"
        >
          AI/ML in Grafana Cloud <Icon name="external-link-alt" />
        </a>{' '}
        documentation.
      </div>
    </PluginPage>
  );
}

const getStyles = (theme: GrafanaTheme2) => {
  return {
    alertLink: css`
      font-weight: bold;
      text-decoration: underline;
      cursor: pointer;
    `,
    topRowContainer: css`
      display: flex;
      gap: 12px;
      margin-bottom: 12px;
    `,
    infoCard: css`
      min-width: 250px;
      max-height: 380px;
    `,
    cardDescription: css`
      min-height: 200px;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
    `,
    cardList: css`
      list-style-type: none;
      padding-left: 0;
    `,
    cardListItem: css`
      margin-bottom: 12px;
    `,
    cardLink: css`
      color: ${theme.colors.text.link};
      cursor: pointer;
      svg {
        margin-left: 4px;
      }
    `,
    grid: css`
      display: grid;
      grid-template-columns: 1fr 250px;
      gap: 12px;
      margin-bottom: 24px;
    `,
    stackedCards: css`
      display: flex;
      flex-direction: column;
      gap: 12px;
      margin-bottom: 24px;
      min-width: 475px;
    `,
    sidebar: css`
      display: flex;
      flex-direction: column;
      gap: 12px;
      margin-bottom: 24px;
      background-color: ${theme.colors.background.secondary};
      height: 100%;
      min-width: 200px;
    `,
    sidebarItem: css`
      padding: 6px;
      margin: 6px;
    `,
    highlightIntro: css`
      border-top: 2px solid ${theme.colors.border.weak};
      padding: 24px 12px;
      margin-bottom: 12px;
      margin-top: 12px;
    `,
    highlight: css`
      display: flex;
      margin-bottom: 12px;
    `,
    highlightInfo: css`
      display: flex;
      flex-direction: column;
      font-weight: bold;
      background-color: ${theme.colors.background.secondary};
      min-width: 200px;
      padding: 24px;
      margin-right: 12px;
      gap: 12px;
    `,
    highlightDetails: css`
      display: flex;
      flex-direction: column;
      padding: 24px 0px;
    `,
    highlightLink: css`
      color: ${theme.colors.text.link};
      font-weight: bold;
      text-decoration: underline;
      cursor: pointer;
    `,
    alert: css`
      display: flex;
      justify-content: space-between;
      gap: 12px;
    `,
  };
};
